import React, { useCallback, useRef, useState } from 'react';
import { Card, Table, Badge, Typography, Space, Row, Col, Statistic, notification, Tag, Modal } from 'antd';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { formatCurrency, formatSquareMeters } from 'utility/utility';
import {
  getPriorityColor,
  getPriorityName,
  getProjectPlanStatusColor,
  getProjectPlanStatusName,
} from 'pages/projects/constants';
import {
  DealBasicDetailsDto,
  DealUnitDto,
  DealPaymentsDto,
  ClientProjectPlansDto,
  ProjectPlanStatus
} from 'api/models';
import { TFunction } from 'i18next';

const { Title, Text } = Typography;

// Updated interface to work with split data structure
interface DealSummaryData {
  basic: DealBasicDetailsDto;
  units?: DealUnitDto[];
  payments?: DealPaymentsDto;
  clientProjectPlans?: ClientProjectPlansDto[];
}

interface PDFContentProps {
  deal: DealSummaryData;
  t: TFunction;
}

const InvisiblePDFContent = React.forwardRef<HTMLDivElement, PDFContentProps>(({ deal, t }, ref) => {
  const unitColumns = [
    {
      title: t('deals:summary-unit-name', 'Unit'),
      dataIndex: 'unitName',
      key: 'unitName',
    },
    {
      title: t('deals:summary-category', 'Category'),
      dataIndex: 'categoryName',
      key: 'categoryName',
    },
    {
      title: t('deals:summary-quantity', 'Quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right' as const,
    },
    {
      title: t('deals:summary-area', 'Area'),
      dataIndex: 'squareMeters',
      key: 'squareMeters',
      align: 'right' as const,
      render: (value: number) => formatSquareMeters(value),
    },
    {
      title: t('deals:summary-price-per-sqm', 'Price per m²'),
      dataIndex: 'negotiatedPricePerSqm',
      key: 'negotiatedPricePerSqm',
      align: 'right' as const,
      render: (value: number) => formatCurrency(value),
    },
    {
      title: t('deals:summary-total-price', 'Total Price'),
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      align: 'right' as const,
      render: (value: number) => formatCurrency(value),
    },
  ];

  const requirementColumns = [
    {
      title: t('deals:summary-requirement-title', 'Title'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('deals:summary-requirement-description', 'Description'),
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
    },
    {
      title: t('deals:summary-requirement-due-date', 'Due Date'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
    },
    {
      title: t('deals:summary-requirement-priority', 'Priority'),
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: number) => (
        <Tag color={getPriorityColor(priority)}>
          {t(`projects:priority-${getPriorityName(priority).key}`, getPriorityName(priority).title)}
        </Tag>
      ),
    },
    {
      title: t('deals:summary-requirement-status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: ProjectPlanStatus) => (
        <Tag color={getProjectPlanStatusColor(status)}>
          {t(
            `projects:plan-status-${getProjectPlanStatusName(status)?.key!}`,
            getProjectPlanStatusName(status)?.title!,
          )}
        </Tag>
      ),
    },
  ];

  return (
    <div
      ref={ref}
      id="pdf-content"
      style={{
        width: '1100px',
        backgroundColor: '#ffffff',
        padding: '20px',
        fontFamily: 'Arial, sans-serif'
      }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            {t('deals:summary-title', 'Deal Summary')}
          </Title>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {t('deals:summary-generated-on', 'Generated on')} {dayjs().format('YYYY-MM-DD HH:mm')}
          </Text>
        </div>

        <Card title={t('deals:summary-overview', 'Deal Overview')} size="small">
          <Row gutter={24}>
            <Col span={8}>
              <div style={{ marginBottom: '16px' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>
                  {t('deals:summary-deal-id', 'Deal ID')}
                </Text>
                <Text strong>#{deal.basic.id}</Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ marginBottom: '16px' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>
                  {t('deals:summary-client', 'Client')}
                </Text>
                <Text strong>{deal.basic.clientName}</Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ marginBottom: '16px' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>
                  {t('deals:summary-project', 'Project')}
                </Text>
                <Text strong>{deal.basic.projectName}</Text>
              </div>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={8}>
              <div style={{ marginBottom: '16px' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>
                  {t('deals:summary-stage', 'Stage')}
                </Text>
                <Text strong>{deal.basic.stage}</Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ marginBottom: '16px' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>
                  {t('deals:summary-created-at', 'Created Date')}
                </Text>
                <Text strong>{dayjs(deal.basic.createdAt).format('YYYY-MM-DD')}</Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ marginBottom: '16px' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>
                  {t('deals:summary-status', 'Status')}
                </Text>
                <Tag color={deal.basic.isActive ? 'green' : 'red'}>
                  {deal.basic.isActive ? t('deals:active', 'Active') : t('deals:inactive', 'Inactive')}
                </Tag>
              </div>
            </Col>
          </Row>
        </Card>

        {deal.payments && (
          <Card title={t('deals:summary-financial-overview', 'Financial Overview')} size="small">
            <Row gutter={24}>
              <Col span={8}>
                <div style={{ textAlign: 'center', padding: '16px', border: '1px solid #f0f0f0', borderRadius: '6px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff', marginBottom: '8px' }}>
                    {formatCurrency(deal.payments.totalAmount || 0)}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>
                    {t('deals:summary-total-amount', 'Total Amount')}
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center', padding: '16px', border: '1px solid #f0f0f0', borderRadius: '6px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a', marginBottom: '8px' }}>
                    {formatCurrency(deal.payments.paidAmount || 0)}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>
                    {t('deals:summary-amount-paid', 'Amount Paid')}
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center', padding: '16px', border: '1px solid #f0f0f0', borderRadius: '6px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14', marginBottom: '8px' }}>
                    {formatCurrency(deal.payments.pendingAmount || 0)}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>
                    {t('deals:summary-amount-pending', 'Amount Pending')}
                  </div>
                </div>
              </Col>
            </Row>

            {deal.payments.nextPaymentDue && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
                <Text strong style={{ color: '#389e0d' }}>
                  {t('deals:summary-next-payment', 'Next Payment Due')}: {' '}
                  {dayjs(deal.payments.nextPaymentDue.dueDate).format('YYYY-MM-DD')} - {' '}
                  {formatCurrency(deal.payments.nextPaymentDue.amount)}
                </Text>
              </div>
            )}
          </Card>
        )}

        {deal.units && deal.units.length > 0 && (
          <Card title={t('deals:summary-units', 'Units')} size="small">
            <Table
              dataSource={deal.units}
              columns={unitColumns}
              pagination={false}
              rowKey={record => `${record.unitId}`}
              size="small"
              style={{ fontSize: '12px' }}
            />

            <div style={{ marginTop: '16px', textAlign: 'right', paddingTop: '12px', borderTop: '2px solid #1890ff' }}>
              <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                {t('deals:summary-units-total', 'Total Units Value')}: {' '}
                {formatCurrency(deal.units.reduce((sum, unit) => sum + (unit.totalPrice || 0), 0))}
              </Text>
            </div>
          </Card>
        )}

        {deal.clientProjectPlans && deal.clientProjectPlans.length > 0 && (
          <Card title={t('deals:summary-unit-requirements', 'Unit Requirements')} size="small">
            <Table
              dataSource={deal.clientProjectPlans}
              columns={requirementColumns}
              pagination={false}
              rowKey={record => `${record.id}`}
              size="small"
              style={{ fontSize: '12px' }}
            />
          </Card>
        )}

        <div style={{ marginTop: '40px', textAlign: 'center', borderTop: '1px solid #f0f0f0', paddingTop: '20px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('deals:summary-footer', 'This document was automatically generated and contains confidential information.')}
          </Text>
        </div>
      </Space>
    </div>
  );
});

InvisiblePDFContent.displayName = 'InvisiblePDFContent';

const DealSummaryPDF = () => {
  const [generating, setGenerating] = useState(false);

  const generatePDF = useCallback(async (props: { deal: DealSummaryData; t: TFunction }) => {
    const { deal, t } = props;

    if (generating) {
      notification.warning({
        message: t('common:pdf-in-progress', 'PDF Generation in Progress'),
        description: t('common:pdf-in-progress-description', 'Please wait for the current PDF generation to complete.'),
      });
      return;
    }

    setGenerating(true);

    try {
      // Create a container for the PDF content
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.top = '-10000px';
      container.style.left = '-10000px';
      container.style.width = '1100px';
      container.style.zIndex = '-1';
      document.body.appendChild(container);

      // Create React component and render it
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(container);

      await new Promise<void>((resolve) => {
        root.render(
          <InvisiblePDFContent deal={deal} t={t} />
        );
        // Wait for rendering
        setTimeout(resolve, 1000);
      });

      const element = container.querySelector('#pdf-content') as HTMLElement;
      if (!element) {
        throw new Error('PDF content element not found');
      }

      // Generate PDF with better quality settings
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 1100,
        height: element.scrollHeight,
        windowWidth: 1100,
        windowHeight: element.scrollHeight,
      });

      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL('image/jpeg', 0.98);

      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pageWidth;
      const imgHeight = (imgProps.height * pageWidth) / imgProps.width;

      let position = 0;
      const pageMargin = 40;
      const contentHeight = pageHeight - pageMargin * 2;

      // Handle multi-page PDFs
      while (position < imgHeight) {
        const remainingHeight = imgHeight - position;
        const currentPageHeight = Math.min(contentHeight, remainingHeight);

        if (position > 0) {
          pdf.addPage();
        }

        pdf.addImage(
          imgData,
          'JPEG',
          0,
          pageMargin - position,
          imgWidth,
          imgHeight
        );

        position += contentHeight;
      }

      // Save the PDF with a descriptive filename
      const filename = `Deal-${deal.basic.id}-${deal.basic.clientName}-Summary-${dayjs().format('YYYY-MM-DD')}.pdf`;
      pdf.save(filename);

      notification.success({
        message: t('common:pdf-generated', 'PDF Generated Successfully'),
        description: t('common:pdf-generated-description', 'Your deal summary has been downloaded successfully.'),
        duration: 3,
      });

      // Cleanup
      root.unmount();
      document.body.removeChild(container);
    } catch (error) {
      console.error('PDF generation error:', error);
      notification.error({
        message: t('common:pdf-error', 'PDF Generation Failed'),
        description: t('common:pdf-error-description', 'There was an error generating the PDF. Please try again.'),
        duration: 5,
      });
    } finally {
      setGenerating(false);
    }
  }, [generating]);

  return { generatePDF, generating };
};

export default DealSummaryPDF;